import { AssignmentApiEndpoints } from "@/views/academy/assignments/AssignmentApiEndpoints";
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from "@/views/academy/api/apiRequests";
import { i18n } from "@/plugins/i18n";
import { AssignmentActionTypes } from "@/views/academy/enums/AssignmentActionTypes";
import { AssignmentStatusTypes } from "@/views/academy/enums/AssignmentStatusTypes";
import { prepareQueryParams } from "@/helpers/prepareQueryParams";

const state = () => ({
  assignmentItemDetails: null,
  isAssignmentTodoModalOpened: false,
  isAssignmentFormModalOpened: false,
  assignments: [],
  sendAssignmentNotificationEmailLoading: false,
  playerTaskDetails: null,
  isDeleteAssignmentDialogOpened: false,
  isCloseAssignmentDialogOpened: false,
  assignmentsListStatus: null,
  isAssignmentTodoModalOpenedReadOnly: false,
  isAssignmentFormModalOpenedReadOnly: false,
  isPlayerTaskDetailsActive: false,
  activePlayerTaskDetailsId: null,
  favoriteAssignment: null
});

const getters = {
  assignmentItemDetails: (state: { assignmentItemDetails: object }) =>
    state.assignmentItemDetails,

  isAssignmentTodoModalOpened: (state: {
    isAssignmentTodoModalOpened: boolean;
  }) => state.isAssignmentTodoModalOpened,

  isAssignmentFormModalOpened: (state: {
    isAssignmentFormModalOpened: boolean;
  }) => state.isAssignmentFormModalOpened,

  assignments: (state: { assignments: any }) => state.assignments,

  sendAssignmentNotificationEmailLoading: (state: {
    sendAssignmentNotificationEmailLoading: any;
  }) => state.sendAssignmentNotificationEmailLoading,

  playerTaskDetails: (state: { playerTaskDetails: any }) =>
    state.playerTaskDetails,

  isDeleteAssignmentDialogOpened: (state: {
    isDeleteAssignmentDialogOpened: boolean;
  }) => state.isDeleteAssignmentDialogOpened,

  isCloseAssignmentDialogOpened: (state: {
    isCloseAssignmentDialogOpened: boolean;
  }) => state.isCloseAssignmentDialogOpened,

  assignmentsListStatus: (state: { assignmentsListStatus: string }) =>
    state.assignmentsListStatus,

  isAssignmentTodoModalOpenedReadOnly: (state: {
    isAssignmentTodoModalOpenedReadOnly: boolean;
  }) => state.isAssignmentTodoModalOpenedReadOnly,

  isAssignmentFormModalOpenedReadOnly: (state: {
    isAssignmentFormModalOpenedReadOnly: boolean;
  }) => state.isAssignmentFormModalOpenedReadOnly,

  isPlayerTaskDetailsActive: (state: { isPlayerTaskDetailsActive: boolean }) =>
    state.isPlayerTaskDetailsActive,

  activePlayerTaskDetailsId: (state: { activePlayerTaskDetailsId: string }) =>
    state.activePlayerTaskDetailsId,

  favoriteAssignment: (state: { favoriteAssignment: object }) =>
    state.favoriteAssignment
};

const mutations = {
  setAssignmentItemDetails: (
    state: { assignmentItemDetails: object },
    payload: object
  ) => {
    state.assignmentItemDetails = payload;
  },

  toggleAssignmentTodoModal: (
    state: {
      isAssignmentTodoModalOpenedReadOnly: any;
      isAssignmentTodoModalOpened: any;
    },
    payload: any
  ) => {
    state.isAssignmentTodoModalOpened = payload.isAssignmentTodoModalOpened;
    state.isAssignmentTodoModalOpenedReadOnly = payload?.readOnly || false;
  },

  toggleAssignmentFormModal: (
    state: {
      isAssignmentFormModalOpenedReadOnly: boolean;
      isAssignmentFormModalOpened: any;
    },
    payload: any
  ) => {
    state.isAssignmentFormModalOpened = payload.isAssignmentFormModalOpened;
    state.isAssignmentFormModalOpenedReadOnly = payload?.readOnly || false;
  },

  setAssignments: (state: { assignments: any }, payload: any) => {
    state.assignments = payload;
  },

  setSendAssignmentNotificationEmailLoading: (
    state: { sendAssignmentNotificationEmailLoading: any },
    payload: any
  ) => {
    state.sendAssignmentNotificationEmailLoading = payload;
  },

  setPlayerTaskDetails: (state: { playerTaskDetails: any }, payload: any) => {
    state.playerTaskDetails = payload;
  },

  toggleDeleteAssignmentDialog: (
    state: {
      isDeleteAssignmentDialogOpened: boolean;
    },
    payload: {
      isDeleteAssignmentDialogOpened: boolean;
    }
  ) => {
    state.isDeleteAssignmentDialogOpened =
      payload.isDeleteAssignmentDialogOpened;
  },

  toggleCloseAssignmentDialog: (
    state: {
      isCloseAssignmentDialogOpened: boolean;
    },
    payload: {
      isCloseAssignmentDialogOpened: boolean;
    }
  ) => {
    state.isCloseAssignmentDialogOpened = payload.isCloseAssignmentDialogOpened;
  },

  setAssignmentsListStatus: (
    state: {
      assignmentsListStatus: string;
    },
    payload: {
      status: string;
    }
  ) => {
    state.assignmentsListStatus = payload.status;
  },

  setIsPlayerTaskDetailsActive: (
    state: {
      isPlayerTaskDetailsActive: boolean;
    },
    payload: {
      isPlayerTaskDetailsActive: boolean;
    }
  ) => {
    state.isPlayerTaskDetailsActive = payload.isPlayerTaskDetailsActive;
  },

  setActivePlayerTaskDetailsId: (
    state: {
      activePlayerTaskDetailsId: string;
    },
    payload: {
      activePlayerTaskDetailsId: string;
    }
  ) => {
    state.activePlayerTaskDetailsId = payload.activePlayerTaskDetailsId;
  },

  setFavoriteAssignment: (
    state: {
      favoriteAssignment: object;
    },
    payload: any
  ) => {
    state.favoriteAssignment = payload;
  }
};

const actions = {
  showAssignmentItemDetails: async (context: {
    dispatch: (arg0: string, arg1: null, arg2: { root: boolean }) => void;
    commit: (
      arg0: string,
      arg1: {} | null,
      arg2?: { root: boolean } | undefined
    ) => void;
  }) => {
    context.commit("root/openRightSidebar", null, { root: true });
    context.commit("setAssignmentItemDetails", {});
  },

  closeAssignmentItemDetails: async (context: {
    dispatch: (arg0: string, arg1: null, arg2: { root: boolean }) => void;
    commit: (arg0: string, arg1: any, arg2?: { root: boolean }) => void;
  }) => {
    context.commit("setAssignmentItemDetails", {});
    context.commit("root/closeRightSidebar", null, { root: true });
  },

  searchPeopleAndTeams: async (
    context: any,
    payload: {
      organizationId: string;
      callback?: Function;
    }
  ) => {
    const { organizationId, callback } = payload;
    try {
      const endpoint = `${AssignmentApiEndpoints.SEARCH_PEOPLE_AND_TEAMS}/${organizationId}/search-people-and-teams`;

      const res: any = await getRequest(endpoint, {
        timeout: 10000
      });

      callback && callback(res.data);
    } catch (e) {
      console.log(e);
    }
  },

  createAssignment: async (
    context: any,
    payload: {
      organizationId: string;
      data: any;
      callback?: Function;
    }
  ) => {
    const { organizationId, data, callback } = payload;
    try {
      const endpoint = `${AssignmentApiEndpoints.CREATE_ASSIGNMENT}/${organizationId}`;
      const res: any = await postRequest(endpoint, data);

      if (res.status === "success") {
        context.dispatch(
          "snackbar/SHOW_SNACKBAR",
          { text: i18n.tc("snackbar_message.added_successfully") },
          {
            root: true
          }
        );
        callback && callback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  updateAssignment: async (
    context: any,
    payload: {
      organizationId: string;
      assignmentId: string;
      data: any;
      callback?: Function;
    }
  ) => {
    const { organizationId, assignmentId, data, callback } = payload;
    try {
      const endpoint = `${AssignmentApiEndpoints.UPDATE_ASSIGNMENT}/${assignmentId}/organization/${organizationId}`;
      const res: any = await putRequest(endpoint, data);

      if (res?.status === "success") {
        setTimeout(() => {
          context.dispatch(
            "snackbar/SHOW_SNACKBAR",
            { text: i18n.tc("snackbar_message.saved_successfully") },
            {
              root: true
            }
          );
        }, 200);

        callback && callback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  getAssignments: async (
    context: any,
    payload: {
      organizationId: any;
      playerId: any;
      status: any;
      filter: any;
      isSearchInput: boolean;
      callback?: Function;
    }
  ) => {
    const { organizationId, playerId, status, filter, callback } = payload;
    let queryParam = "";
    let prefix = "?";
    if (status) {
      queryParam += `${prefix}status=${status}`;
      context.commit("setAssignmentsListStatus", { status });
    } else if (filter) {
      prefix = queryParam.includes("?") ? "&" : "?";
      queryParam += `${prefix}filter=${filter}`;
    }
    queryParam += `${queryParam.includes("?") ? "&" : "?"}pageSize=50`;
    const isPlayerAssignments = playerId ? `/player/${playerId}` : "";
    try {
      const endpoint = `${AssignmentApiEndpoints.GET_ALL_ASSIGNMENTS}/${organizationId}${isPlayerAssignments}${queryParam}`;
      const res: any = await getRequest(endpoint);
      context.commit("setAssignments", res.data);
      callback && callback(res.data);
    } catch (e) {
      console.log(e);
    }
  },

  getAssignmentDetails: async (
    context: any,
    payload: {
      organizationId: string;
      assignmentId: string;
      callback?: Function;
    }
  ) => {
    const { organizationId, assignmentId, callback } = payload;
    try {
      const endpoint = `${AssignmentApiEndpoints.GET_ASSIGNMENT_DETAILS}/${assignmentId}/organization/${organizationId}`;
      const res: any = await getRequest(endpoint);

      callback && callback(res);
    } catch (e) {
      console.log(e);
    }
  },

  getAssigneesTeamsAndPlayers: async (
    context: any,
    payload: {
      organizationId: string;
      assignmentId: string;
      callback?: Function;
    }
  ) => {
    const { organizationId, assignmentId, callback } = payload;
    try {
      const endpoint = `${AssignmentApiEndpoints.GET_ASSIGNMENT_TEAMS_AND_PLAYERS}/${organizationId}/assignment/${assignmentId}`;
      const res: any = await getRequest(endpoint);

      callback && callback(res);
    } catch (e) {
      console.log(e);
    }
  },

  sendAssignmentNotificationEmail: async (
    context: any,
    payload: { organizationId: any; assignmentId: any; players: any }
  ) => {
    context.commit("setSendAssignmentNotificationEmailLoading", true);
    const { organizationId, assignmentId, players } = payload;
    try {
      const endpoint = `${AssignmentApiEndpoints.SEND_ASSIGNMENT_NOTIFICATION_EMAIL}/${assignmentId}/organization/${organizationId}`;
      const res: any = await postRequest(endpoint, players);
      if (res.status === "success") {
        context.commit("setSendAssignmentNotificationEmailLoading", false);
        context.dispatch(
          "snackbar/SHOW_SNACKBAR",
          { text: i18n.tc("snackbar_message.reminded_successfully") },
          {
            root: true
          }
        );
      }
    } catch (e) {
      console.log(e);
    }
  },

  getPlayerTaskDetails: async (
    context: any,
    payload: { organizationId: any; assignmentId: any; playerId: string }
  ) => {
    const { organizationId, assignmentId, playerId } = payload;
    try {
      const endpoint = `${AssignmentApiEndpoints.GET_PLAYER_TASK_DETAILS}/${assignmentId}/organization/${organizationId}/player/${playerId}`;
      const res: any = await getRequest(endpoint);
      context.commit("setPlayerTaskDetails", res?.data);
    } catch (e) {
      console.log(e);
    }
  },

  getPlayerAssignments: async (
    context: any,
    payload: {
      organizationId: string;
      playerId: string;
      status: string;
    }
  ) => {
    const { organizationId, playerId, status } = payload;
    try {
      const endpoint = `${AssignmentApiEndpoints.GET_PLAYER_ASSIGNMENTS}/${organizationId}/player/${playerId}?status=${status}&pageSize=50`;
      const res: any = await getRequest(endpoint);
      context.commit("setAssignments", res.data);
    } catch (e) {
      console.log(e);
    }
  },

  getPlayerAssignmentTask: async (
    context: any,
    payload: {
      taskResponseId: string;
      playerId: string;
      callback?: Function;
    }
  ) => {
    const { taskResponseId, playerId, callback } = payload;

    try {
      const endpoint = `${AssignmentApiEndpoints.GET_PLAYER_ASSIGNMENT_FORM}/${taskResponseId}/player/${playerId}`;
      const res: any = await getRequest(endpoint);

      callback && callback(res.data);
    } catch (e) {
      console.log(e);
    }
  },

  savePlayerAssignmentResponse: async (
    context: any,
    payload: {
      taskResponseId: string;
      playerId: string;
      data: any;
      callback?: Function;
    }
  ) => {
    const { taskResponseId, playerId, data, callback } = payload;
    try {
      const endpoint = `${AssignmentApiEndpoints.GET_PLAYER_ASSIGNMENT_FORM}/${taskResponseId}/player/${playerId}`;
      const res: any = await postRequest(endpoint, data);

      if (res.status === 200) {
        setTimeout(() => {
          context.dispatch(
            "snackbar/SHOW_SNACKBAR",
            { text: i18n.tc("snackbar_message.saved_successfully") },
            {
              root: true
            }
          );
        }, 200);
      }

      callback && callback(res.data);
    } catch (e) {
      console.log(e);
    }
  },

  closeOrDeleteAssignment: async (
    context: any,
    payload: { assignmentId: any; organizationId: any; actionType: any }
  ) => {
    try {
      const { assignmentId, organizationId, actionType } = payload;
      const res: any = await deleteRequest(
        `${AssignmentApiEndpoints.CLOSE_OR_REMOVE_ASSIGNMENT}/${assignmentId}/organization/${organizationId}?actionType=${actionType}`
      );
      if (res.status === "success") {
        context.commit("root/closeRightSidebar", null, { root: true });
        context.dispatch("getAssignments", {
          organizationId,
          status: AssignmentStatusTypes.OPEN
        });
        context.dispatch(
          "snackbar/SHOW_SNACKBAR",
          {
            text: i18n.tc(
              `${
                actionType === AssignmentActionTypes.REMOVE
                  ? "snackbar_message.removed_successfully"
                  : "snackbar_message.closed_successfully"
              }`
            )
          },
          {
            root: true
          }
        );
        if (actionType === AssignmentActionTypes.REMOVE) {
          context.dispatch("academyNavigator/fetchFavoriteStorageItems", null, {
            root: true
          });
        }
      }
    } catch (e) {
      console.log(e);
    }
  },

  toggleFavoriteAssignment: async (
    context: {
      dispatch: (
        arg0: string,
        arg1: { assignmentId?: any; text?: any } | null,
        arg2?: { root: boolean } | undefined
      ) => void;
    },
    payload: {
      assignmentId: any;
      organizationId: any;
      playerId: any;
      isAssignmentFavorite: any;
    }
  ) => {
    try {
      const {
        assignmentId,
        organizationId,
        playerId,
        isAssignmentFavorite
      } = payload;
      let res: any = null;
      if (isAssignmentFavorite) {
        const query = prepareQueryParams({ playerId });
        res = await deleteRequest(
          `${AssignmentApiEndpoints.DELETE_FAVORITE_ASSIGNMENT}/${assignmentId}${query}`
        );
      } else {
        const body = {
          playerId: playerId || null
        };
        res = await postRequest(
          `${AssignmentApiEndpoints.CREATE_FAVORITE_ASSIGNMENT}/${assignmentId}/organization/${organizationId}`,
          body
        );
      }
      if (res?.status === "success") {
        setTimeout(() => {
          context.dispatch("academyNavigator/fetchFavoriteStorageItems", null, {
            root: true
          });
          context.dispatch("fetchFavoriteAssignment", { assignmentId });
          context.dispatch(
            "snackbar/SHOW_SNACKBAR",
            {
              text: isAssignmentFavorite
                ? i18n.tc("snackbar_message.removed_successfully")
                : i18n.tc("snackbar_message.added_successfully")
            },
            {
              root: true
            }
          );
        }, 500);
      }
    } catch (e) {
      console.log(e);
    }
  },
  fetchFavoriteAssignment: async (
    context: {
      commit: (
        arg0: string,
        arg1: { assignmentId: any; isAssignmentFavorite: any }
      ) => void;
    },
    payload: { assignmentId: any; playerId: string }
  ) => {
    try {
      const { assignmentId, playerId } = payload;
      const query = prepareQueryParams({ playerId });
      const res: any = await getRequest(
        `${AssignmentApiEndpoints.CHECK_ASSIGNMENT_IS_FAVORITE}/${assignmentId}${query}`
      );
      context.commit("setFavoriteAssignment", {
        assignmentId,
        isAssignmentFavorite: res.data
      });
    } catch (e) {
      console.log(e);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
